import { appConfig } from '@/config/app';
import { publishEvent } from '@/modules/events/eventBus.service';
import type {
  CommercialOperationEventPayload,
  ErpEventName,
  ErpEventPayloadMap,
  InvoiceEventPayload,
  LogisticsScheduledPayload,
  PermissionDeniedPayload,
  ReservationConflictPayload,
  ReservationEventPayload,
} from '@/modules/events/types';
import { getAuditActor } from '@/services/audit/auditContext';

function basePayload(actorUid: string, actorEmail?: string) {
  return {
    actorUid,
    ...(actorEmail ? { actorEmail } : {}),
    companyId: appConfig.companyId,
    occurredAt: new Date().toISOString(),
  };
}

function actorFromContext(): { uid: string; email?: string } | null {
  const actor = getAuditActor();
  if (actor) return { uid: actor.uid, email: actor.email };
  return null;
}

export async function publishDomainEvent<T extends ErpEventName>(
  name: T,
  payload: Omit<ErpEventPayloadMap[T], keyof ReturnType<typeof basePayload>>,
  options?: { idempotencyKey?: string },
): Promise<void> {
  const actor = actorFromContext();
  if (!actor) return;

  const full = {
    ...basePayload(actor.uid, actor.email),
    ...payload,
  } as ErpEventPayloadMap[T];

  await publishEvent(name, full, options);
}

function eventOptions(idempotencyKey?: string): { idempotencyKey?: string } | undefined {
  return idempotencyKey ? { idempotencyKey } : undefined;
}

export function publishReservationCreated(
  data: Omit<ReservationEventPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
  idempotencyKey?: string,
): Promise<void> {
  return publishDomainEvent('reservation.created', data, eventOptions(idempotencyKey));
}

export function publishReservationCancelled(
  data: Omit<ReservationEventPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
  idempotencyKey?: string,
): Promise<void> {
  return publishDomainEvent('reservation.cancelled', data, eventOptions(idempotencyKey));
}

export function publishReservationConflict(
  data: Omit<ReservationConflictPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
  idempotencyKey?: string,
): Promise<void> {
  return publishDomainEvent('reservation.conflict', data, eventOptions(idempotencyKey));
}

export function publishCommercialOperationCreated(
  data: Omit<
    CommercialOperationEventPayload,
    'actorUid' | 'companyId' | 'occurredAt'
  >,
): Promise<void> {
  return publishDomainEvent('commercial.operation.created', data);
}

export function publishLogisticsScheduled(
  data: Omit<LogisticsScheduledPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
): Promise<void> {
  return publishDomainEvent('logistics.scheduled', data);
}

export function publishInvoiceCreated(
  data: Omit<InvoiceEventPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
): Promise<void> {
  return publishDomainEvent('invoice.created', data);
}

export function publishInvoicePending(
  data: Omit<InvoiceEventPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
): Promise<void> {
  return publishDomainEvent('invoice.pending', data);
}

export function publishPermissionDenied(
  data: Omit<PermissionDeniedPayload, 'actorUid' | 'companyId' | 'occurredAt'>,
  idempotencyKey: string,
): Promise<void> {
  return publishDomainEvent('user.permission_denied', data, { idempotencyKey });
}
